import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import useColorService from '../../app/services/hooks/useColorService';
import { Color, ColorFilters, defaultColorFilters } from '../../app/models/Color';
import ColorDataTable from './components/tables/ColorDataTable';
import ColorEdit from './components/ColorEdit';
import ColorCreate from './components/ColorCreate';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Colores',
        url: '/colors',
        isActive: true
    }
];

const Colors = () => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { fetchingGetColors, getColors, deleteColor } = useColorService();
    const [colors, setColors] = useState<Color[]>([]);
    const [colorIdEdit, setColorIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);
    const { navigationPage } = useNavigationPage();

    const [colorFilter, setColorFilter] = useState<ColorFilters>(defaultColorFilters);

    useEffect(() => {
        reloadTable();
    }, [colorFilter]);

    const getAllColors = () => {
        getColors(colorFilter, {
            onSuccess: (response: ServiceResponse) => {
                setColors(response.data.colors);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (colorId: number) => {
        setShowingEdit(true);
        setColorIdEdit(colorId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setColorIdEdit(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const reloadTable = () => {
        getAllColors();
    };

    const destroy = (colorId: number) => {
        const _text = 'Está a punto de eliminar el color #' + colorId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando color...');
                        deleteColor(colorId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El color no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Colores" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nuevo color" />}
                />
                <ColorDataTable
                    colors={colors}
                    edit={showEdit}
                    destroy={destroy}
                    loading={fetchingGetColors}
                    filter={colorFilter}
                    setFilter={setColorFilter}
                    totalRows={totalRows}
                    callbackSuccess={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Color"
                    backdrop={true}
                    showFooter={false}
                >
                    <ColorCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Color"
                    backdrop={true}
                    showFooter={false}
                >
                    <ColorEdit
                        colorId={colorIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default Colors;
